/**
 * Description middle info component.
 * Displays subscription status, date, catchup availability, and age code information
 * in the middle section of content descriptions.
 */
import Addon from '../../../../shared/classes/addon';
import AgeCode from '../ageCode/ageCode';
import CatchupAvailability from '../catchup/catchupAvailability';
import Container from '../../atoms/container/descriptionMiddleInfoContainer';
import Date from '../../atoms/text/descriptionContentDate';
import MainContext from '../../../../shared/components/utils/context/mainContext';
import Subscription from '../addons/descriptionAddonSubscription';
import { useContext, useMemo } from 'react';

/**
 * Props for the DescriptionMiddleInfo component.
 *
 * @param {string} date - Date of the content
 * @param {boolean} catchup - Whether the content is catchup available
 * @param {string} ageCode - Age code of the content
 */
interface Props {
  date?: string;
  catchup?: boolean;
  ageCode?: string;
}

/**
 * Renders the middle information section of content descriptions.
 * Shows subscription status, date, catchup availability, and age code information.
 *
 * @returns {JSX.Element} Description middle info component
 */
export default function DescriptionMiddleInfo(parentProps: Props) {
  // Hook
  const { display, props, date } = useMiddleInfo(parentProps);

  // Render
  return (
    <Container id='descriptionMiddleInfo'>
      <Subscription {...props.subscription} />
      {display.date ? <Date>{date}</Date> : null}
      {display.catchup ? <CatchupAvailability {...props.catchup} /> : null}
      {display.ageCode ? <AgeCode {...props.ageCode} /> : null}
    </Container>
  );
}

/**
 * Hook to get the middle information of the currently focused asset in a rail.
 */
function useMiddleInfo(parentProps: Props) {
  // Context
  const { asset } = useContext(MainContext);

  // Memoize the middle information
  return useMemo(() => {
    const { date, catchup, ageCode } = parentProps;
    return {
      date,
      display: {
        ageCode: !!ageCode,
        catchup: !!catchup,
        date: !!date,
      },
      props: {
        ageCode: {
          ageCode,
          margin: !!(date || catchup),
        },
        catchup: {
          date: asset?.date?.start,
          displaySeparator: !!date,
        },
        subscription: {
          asset: asset as Addon,
        },
      },
    };
  }, [asset, parentProps]);
}
